import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonIcon,
  IonText,
  IonButton,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonBadge,
  IonItem,
  IonList,
  IonSpinner,
  IonChip,
  IonButtons,
  IonMenuButton,
} from '@ionic/react';
import {
  peopleOutline,
  trendingUpOutline,
  calendarOutline,
  cashOutline,
  cartOutline,
  personOutline,
  shieldCheckmarkOutline,
  timeOutline,
  statsChartOutline,
  layersOutline,
  ticketOutline,
  businessOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { adminApiService, UserWithStats, SystemStats, InventoryOverview } from '../services/adminApi';
import { getCurrencySymbol, DEFAULT_CURRENCY } from '../utils/currencyConstants';
import { format } from 'date-fns';
import CenteredLoader from '../components/CenteredLoader';
import { useAuth } from '../contexts/AuthContext';

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSegment, setSelectedSegment] = useState<'overview' | 'users' | 'inventory'>('overview');
  
  // State for data
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [inventoryOverview, setInventoryOverview] = useState<InventoryOverview | null>(null);

  // Check if user is admin - temporarily disabled for debugging
  useEffect(() => {
    console.log('AdminDashboard - Current user:', user);
    console.log('User role:', user?.role);
    console.log('Is admin?', user?.role === 'admin');
    
    // Temporarily allow access for debugging
    // if (user && user.role !== 'admin') {
    //   history.replace('/dashboard');
    //   return;
    // }
  }, [user, history]);

  useEffect(() => {
    // Temporarily allow loading data for any user for debugging
    if (user) {
      loadData();
    }
  }, [user, selectedSegment]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (selectedSegment === 'overview') {
        const stats = await adminApiService.getSystemStats();
        setSystemStats(stats);
      } else if (selectedSegment === 'users') {
        const usersData = await adminApiService.getAllUsers();
        setUsers(usersData);
      } else if (selectedSegment === 'inventory') {
        const inventoryData = await adminApiService.getInventoryOverview();
        setInventoryOverview(inventoryData);
      }
    } catch (err: any) {
      console.error('Failed to load admin data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `${getCurrencySymbol(DEFAULT_CURRENCY)}${amount.toFixed(2)}`;
  };

  const getActivityStatus = (isActive: boolean) => {
    return isActive ? (
      <IonChip color="success">
        <IonIcon icon={shieldCheckmarkOutline} />
        <IonLabel>Active</IonLabel>
      </IonChip>
    ) : (
      <IonChip color="medium">
        <IonIcon icon={timeOutline} />
        <IonLabel>Inactive</IonLabel>
      </IonChip>
    );
  };

  const getRoleChip = (role: string) => {
    return role === 'admin' ? (
      <IonChip color="danger">
        <IonIcon icon={shieldCheckmarkOutline} />
        <IonLabel>Admin</IonLabel>
      </IonChip>
    ) : (
      <IonChip color="primary">
        <IonIcon icon={personOutline} />
        <IonLabel>Seller</IonLabel>
      </IonChip>
    );
  };

  // Temporarily disable access check for debugging
  if (false && user?.role !== 'admin') {
    return (
      <IonPage>
        <IonContent>
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <IonIcon icon={shieldCheckmarkOutline} className="text-6xl text-red-500 mb-4" />
              <h2 className="text-xl font-semibold text-red-600 mb-2">Access Denied</h2>
              <p className="text-gray-600 dark:text-gray-400">You don't have admin permissions.</p>
              <p className="text-sm text-gray-500 mt-2">Debug: Role = {user?.role}</p>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (loading && !systemStats && !users.length) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonMenuButton />
            </IonButtons>
            <IonTitle>Admin Dashboard</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <CenteredLoader message="Loading admin data..." />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Admin Dashboard</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="bg-gray-50 dark:bg-gray-900">
        {/* Segment Control */}
        <div className="px-4 pt-4 mb-4">
          <IonSegment
            value={selectedSegment}
            onIonChange={(e) => setSelectedSegment(e.detail.value as 'overview' | 'users' | 'inventory')}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm"
          >
            <IonSegmentButton value="overview">
              <IonIcon icon={statsChartOutline} />
              <IonLabel>Overview</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="users">
              <IonIcon icon={peopleOutline} />
              <IonLabel>Users</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="inventory">
              <IonIcon icon={layersOutline} />
              <IonLabel>Inventory</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </div>

        {error && (
          <div className="mx-4 mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* System Overview Tab */}
        {selectedSegment === 'overview' && systemStats && (
          <div className="px-4">
            {/* System Stats Cards */}
            <IonGrid>
              <IonRow>
                <IonCol size="12" sizeMd="6" sizeLg="3">
                  <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
                    <IonCardContent className="flex items-center justify-between p-6">
                      <div className="text-5xl text-primary">
                        <IonIcon icon={peopleOutline} />
                      </div>
                      <div className="text-right flex-1 ml-4">
                        <span className="block text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                          {systemStats.totalUsers}
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Total Users
                        </p>
                        <p className="text-xs text-green-600">
                          {systemStats.activeUsers} active
                        </p>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                <IonCol size="12" sizeMd="6" sizeLg="3">
                  <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
                    <IonCardContent className="flex items-center justify-between p-6">
                      <div className="text-5xl text-primary">
                        <IonIcon icon={cashOutline} />
                      </div>
                      <div className="text-right flex-1 ml-4">
                        <span className="block text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                          {formatCurrency(systemStats.totalRevenue)}
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Total Revenue
                        </p>
                        <p className="text-xs text-green-600">
                          {formatCurrency(systemStats.revenueThisMonth)} this month
                        </p>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                <IonCol size="12" sizeMd="6" sizeLg="3">
                  <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
                    <IonCardContent className="flex items-center justify-between p-6">
                      <div className="text-5xl text-primary">
                        <IonIcon icon={cartOutline} />
                      </div>
                      <div className="text-right flex-1 ml-4">
                        <span className="block text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                          {systemStats.totalSales}
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Total Sales
                        </p>
                        <p className="text-xs text-green-600">
                          {systemStats.salesThisMonth} this month
                        </p>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                <IonCol size="12" sizeMd="6" sizeLg="3">
                  <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
                    <IonCardContent className="flex items-center justify-between p-6">
                      <div className="text-5xl text-primary">
                        <IonIcon icon={calendarOutline} />
                      </div>
                      <div className="text-right flex-1 ml-4">
                        <span className="block text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                          {systemStats.totalEvents}
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Total Events
                        </p>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>
              </IonRow>
            </IonGrid>

            {/* Top Users */}
            <IonCard className="mt-4">
              <IonCardHeader>
                <IonCardTitle className="flex items-center">
                  <IonIcon icon={trendingUpOutline} className="mr-2" />
                  Top Users by Revenue
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList>
                  {systemStats.topUsers.slice(0, 10).map((topUser, index) => (
                    <IonItem key={topUser._id} button onClick={() => history.push(`/admin/users/${topUser._id}`)}>
                      <IonBadge slot="start" color="primary">{index + 1}</IonBadge>
                      <IonLabel>
                        <h2>{topUser.user.profile?.name || topUser.user.email}</h2>
                        <p>{topUser.user.email}</p>
                      </IonLabel>
                      <div slot="end" className="text-right">
                        <p className="font-semibold text-green-600">{formatCurrency(topUser.totalRevenue)}</p>
                        <p className="text-sm text-gray-500">{topUser.salesCount} sales</p>
                      </div>
                    </IonItem>
                  ))}
                </IonList>
              </IonCardContent>
            </IonCard>
          </div>
        )}

        {/* Users Tab */}
        {selectedSegment === 'users' && (
          <div className="px-4">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <IonIcon icon={peopleOutline} className="mr-2" />
                    User Management
                  </div>
                  <IonBadge color="primary">{users.length} users</IonBadge>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <IonSpinner />
                  </div>
                ) : (
                  <IonList>
                    {users.map((userData) => (
                      <IonItem key={userData._id} button onClick={() => history.push(`/admin/users/${userData._id}`)}>
                        <div className="w-full py-2">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h2 className="font-semibold text-lg">
                                {userData.profile?.name || userData.email}
                              </h2>
                              <p className="text-gray-600 dark:text-gray-400">{userData.email}</p>
                            </div>
                            <div className="flex space-x-2">
                              {getRoleChip(userData.role)}
                              {getActivityStatus(userData.isActive)}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Events</p>
                              <p className="font-semibold">{userData.totalEvents}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Sales</p>
                              <p className="font-semibold">{userData.totalSales}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Inventory</p>
                              <p className="font-semibold">{userData.currentInventory}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Revenue</p>
                              <p className="font-semibold text-green-600">
                                {formatCurrency(userData.revenueThisMonth)}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Last Login</p>
                              <p className="font-semibold text-xs">
                                {format(new Date(userData.lastLogin), 'MMM dd')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </IonCardContent>
            </IonCard>
          </div>
        )}

        {/* Inventory Overview Tab */}
        {selectedSegment === 'inventory' && inventoryOverview && (
          <div className="px-4">
            {/* Inventory Stats Cards */}
            <IonGrid>
              <IonRow>
                <IonCol size="12" sizeMd="6" sizeLg="3">
                  <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
                    <IonCardContent className="flex items-center justify-between p-6">
                      <div className="text-5xl text-primary">
                        <IonIcon icon={ticketOutline} />
                      </div>
                      <div className="text-right flex-1 ml-4">
                        <span className="block text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                          {inventoryOverview.overview.totalTickets}
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Total Tickets
                        </p>
                        <p className="text-xs text-green-600">
                          {inventoryOverview.overview.availableTickets} available
                        </p>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                <IonCol size="12" sizeMd="6" sizeLg="3">
                  <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
                    <IonCardContent className="flex items-center justify-between p-6">
                      <div className="text-5xl text-success">
                        <IonIcon icon={cashOutline} />
                      </div>
                      <div className="text-right flex-1 ml-4">
                        <span className="block text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                          {formatCurrency(inventoryOverview.overview.totalInventoryValue)}
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Inventory Value
                        </p>
                        <p className="text-xs text-green-600">
                          {formatCurrency(inventoryOverview.overview.averageTicketValue)} avg/ticket
                        </p>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                <IonCol size="12" sizeMd="6" sizeLg="3">
                  <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
                    <IonCardContent className="flex items-center justify-between p-6">
                      <div className="text-5xl text-warning">
                        <IonIcon icon={cartOutline} />
                      </div>
                      <div className="text-right flex-1 ml-4">
                        <span className="block text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                          {inventoryOverview.overview.soldTickets}
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Sold Tickets
                        </p>
                        <p className="text-xs text-green-600">
                          {formatCurrency(inventoryOverview.overview.totalRevenue)} revenue
                        </p>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                <IonCol size="12" sizeMd="6" sizeLg="3">
                  <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
                    <IonCardContent className="flex items-center justify-between p-6">
                      <div className="text-5xl text-medium">
                        <IonIcon icon={timeOutline} />
                      </div>
                      <div className="text-right flex-1 ml-4">
                        <span className="block text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                          {inventoryOverview.overview.reservedTickets}
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Reserved
                        </p>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>
              </IonRow>
            </IonGrid>

            {/* Top Categories */}
            <IonCard className="mt-4">
              <IonCardHeader>
                <IonCardTitle className="flex items-center">
                  <IonIcon icon={layersOutline} className="mr-2" />
                  Top Categories
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList>
                  {inventoryOverview.topCategories.slice(0, 10).map((category, index) => (
                    <IonItem key={index}>
                      <IonLabel>
                        <h2 className="font-semibold">{category.category}</h2>
                        <p className="text-sm text-gray-600">
                          {category.count} tickets • {category.userCount} users
                        </p>
                      </IonLabel>
                      <div slot="end" className="text-right">
                        <p className="font-semibold text-green-600">
                          {formatCurrency(category.value)}
                        </p>
                        <IonBadge color="primary">#{index + 1}</IonBadge>
                      </div>
                    </IonItem>
                  ))}
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Top Users by Inventory */}
            <IonCard className="mt-4">
              <IonCardHeader>
                <IonCardTitle className="flex items-center">
                  <IonIcon icon={peopleOutline} className="mr-2" />
                  Top Users by Inventory Value
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList>
                  {inventoryOverview.topUsersByInventory.slice(0, 10).map((userStat, index) => (
                    <IonItem 
                      key={userStat._id} 
                      button 
                      onClick={() => history.push(`/admin/users/${userStat._id}`)}
                    >
                      <IonLabel>
                        <h2 className="font-semibold">
                          {userStat.user.profile?.name || userStat.user.email}
                        </h2>
                        <p className="text-sm text-gray-600">
                          {userStat.totalTickets} tickets
                        </p>
                      </IonLabel>
                      <div slot="end" className="text-right">
                        <p className="font-semibold text-green-600">
                          {formatCurrency(userStat.totalValue)}
                        </p>
                        <IonBadge color="secondary">#{index + 1}</IonBadge>
                      </div>
                    </IonItem>
                  ))}
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Top Suppliers */}
            {inventoryOverview.topSuppliers.length > 0 && (
              <IonCard className="mt-4">
                <IonCardHeader>
                  <IonCardTitle className="flex items-center">
                    <IonIcon icon={businessOutline} className="mr-2" />
                    Top Suppliers by Volume
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList>
                    {inventoryOverview.topSuppliers.slice(0, 10).map((supplierStat, index) => (
                      <IonItem key={supplierStat._id}>
                        <IonLabel>
                          <h2 className="font-semibold">{supplierStat.supplier.name}</h2>
                          <p className="text-sm text-gray-600">
                            {supplierStat.ticketCount} tickets
                            {supplierStat.supplier.email && ` • ${supplierStat.supplier.email}`}
                          </p>
                        </IonLabel>
                        <div slot="end" className="text-right">
                          <p className="font-semibold text-green-600">
                            {formatCurrency(supplierStat.totalValue)}
                          </p>
                          <IonBadge color="tertiary">#{index + 1}</IonBadge>
                        </div>
                      </IonItem>
                    ))}
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}
          </div>
        )}

        {loading && selectedSegment === 'inventory' && !inventoryOverview && (
          <div className="px-4">
            <CenteredLoader message="Loading inventory data..." />
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default AdminDashboard;